import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CrearCotizacionServiciosVista } from '../vista/crearCotizacionServiciosVista';
import {
    ascensorCompleto,
    construirOpcionesCotizacion,
    cotizacionServiciosInicial,
    escaleraCompleta,
    nuevaEscalera,
    nuevoAscensor,
    resultadoSinCalcular,
    type ITablaEquiposMeta,
    type TipoEquipoServicio
} from '../interfaces';
import { configuracionServiciosInicial } from '../../configuracionCotizadorServicios/interfaces';
import type { ICotizacionServicios } from '../../../modelos/CotizadorServicios/entidades/ICotizacionServicios';
import type { IEquipoServicioAscensor } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioAscensor';
import type { IEquipoServicioEscalera } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioEscalera';
import type { IOpcionSelectServicio } from '../../../modelos/CotizadorServicios/entidades/IOpcionesCotizacionServicios';
import { UbicacionModelo } from '../../../modelos/Ubicacion';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { useConfirmacion } from '../../../globales/contextos/confirmacionContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';

interface CrearCotizacionServiciosCVProps {
    /** Cotización a editar; sin ella se crea una nueva. */
    cotizacionInicial?: ICotizacionServicios;
    alGuardar: (cotizacion: ICotizacionServicios) => void;
    alVolver: () => void;
}

type ListaEquipos = 'Ascensores' | 'Escaleras';
const LISTA: Record<TipoEquipoServicio, ListaEquipos> = { ascensor: 'Ascensores', escalera: 'Escaleras' };

/** Estado de la cotización, edición de equipos y acciones del paso a paso. */
export const CrearCotizacionServiciosCV: React.FC<CrearCotizacionServiciosCVProps> = ({ cotizacionInicial, alGuardar, alVolver }) => {
    const { usuario } = useAutenticacion();
    const { mostrarNotificacion } = useNotificaciones();
    const { confirmar } = useConfirmacion();

    const [cotizacion, setCotizacion] = useState<ICotizacionServicios>(() => cotizacionInicial ?? {
        ...cotizacionServiciosInicial,
        PaisId: usuario?.PaisId,
        FechaCreacion: new Date().toISOString(),
        UsuarioCreadorId: usuario?.Id,
        UsuarioCreadorNombre: usuario?.NombreCompleto
    });
    const [errores, setErrores] = useState<Record<string, string>>({});
    const [requiereRecalculo, setRequiereRecalculo] = useState(false);
    const [paises, setPaises] = useState<IOpcionSelectServicio[]>([]);

    // Edición en línea de equipos: una sola fila a la vez.
    const [filaEnEdicion, setFilaEnEdicion] = useState<{ tipo: TipoEquipoServicio; id: string } | null>(null);
    const [respaldoFila, setRespaldoFila] = useState<IEquipoServicioAscensor | IEquipoServicioEscalera | null>(null);
    const [intentoGuardar, setIntentoGuardar] = useState(false);

    // TODO back: la configuración (y con ella las opciones) se consulta del servicio del cotizador.
    const configuracion = configuracionServiciosInicial;
    const opciones = useMemo(() => construirOpcionesCotizacion(configuracion), [configuracion]);

    useEffect(() => {
        const cargarPaises = async () => {
            try {
                const respuesta = await new UbicacionModelo().obtenerPaises();
                setPaises((respuesta?.Data ?? []).map(p => ({ value: String(p.Id), label: p.Nombre })));
            } catch {
                mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'No fue posible consultar los países.', 5000);
            }
        };
        cargarPaises();
    }, []);

    const nombrePais = paises.find(p => p.value === String(cotizacion.PaisId))?.label;

    /** Todo cambio posterior a un cálculo deja los valores desactualizados. */
    const hayResultado = !!cotizacion.Resultado;
    const actualizarCotizacion = useCallback((cambio: (prev: ICotizacionServicios) => ICotizacionServicios) => {
        if (hayResultado) setRequiereRecalculo(true);
        setCotizacion(cambio);
    }, [hayResultado]);

    const alCambiar = useCallback(<K extends keyof ICotizacionServicios>(campo: K, valor: ICotizacionServicios[K]) => {
        actualizarCotizacion(prev => ({ ...prev, [campo]: valor }));
        setErrores(prev => ({ ...prev, [campo]: '' }));
    }, [actualizarCotizacion]);

    // --- Validaciones por paso ---

    const validarDatosGenerales = () => {
        const nuevosErrores: Record<string, string> = {};
        if (!cotizacion.Proyecto.trim()) nuevosErrores.Proyecto = 'El proyecto es obligatorio';
        if (!cotizacion.Cliente.trim()) nuevosErrores.Cliente = 'El cliente es obligatorio';
        if (!cotizacion.PaisId) nuevosErrores.PaisId = 'El país es obligatorio';
        if (!cotizacion.Anio || cotizacion.Anio < 2025) nuevosErrores.Anio = 'El año debe ser 2025 o posterior';
        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const validarEquipos = () => {
        if (cotizacion.Ascensores.length + cotizacion.Escaleras.length > 0) return true;
        mostrarNotificacion(EnumTipoNotificacion.Warning, 'Sin equipos', 'Agrega al menos un ascensor o una escalera para continuar.');
        return false;
    };

    // --- Edición de equipos ---

    const cancelarEdicion = useCallback(() => {
        if (!filaEnEdicion) return;
        const lista = LISTA[filaEnEdicion.tipo];
        actualizarCotizacion(prev => ({
            ...prev,
            // Sin respaldo la fila era nueva: se descarta.
            [lista]: respaldoFila
                ? (prev[lista] as { Id: string }[]).map(e => e.Id === filaEnEdicion.id ? respaldoFila : e)
                : (prev[lista] as { Id: string }[]).filter(e => e.Id !== filaEnEdicion.id)
        }));
        setFilaEnEdicion(null);
        setRespaldoFila(null);
        setIntentoGuardar(false);
    }, [filaEnEdicion, respaldoFila, actualizarCotizacion]);

    const agregarEquipo = (tipo: TipoEquipoServicio) => {
        const equipo = tipo === 'ascensor' ? nuevoAscensor() : nuevaEscalera();
        actualizarCotizacion(prev => ({ ...prev, [LISTA[tipo]]: [...prev[LISTA[tipo]], equipo] }));
        setRespaldoFila(null);
        setIntentoGuardar(false);
        setFilaEnEdicion({ tipo, id: equipo.Id });
    };

    const crearMeta = <T extends { Id: string }>(tipo: TipoEquipoServicio, completo: (equipo: T) => boolean): ITablaEquiposMeta<T> => {
        const lista = LISTA[tipo];
        const equipos = cotizacion[lista] as unknown as T[];
        return {
            opciones,
            filaEnEdicion: filaEnEdicion?.tipo === tipo ? filaEnEdicion.id : null,
            intentoGuardar,
            soloLectura: false,
            alCambiar: (id, campo, valor) => actualizarCotizacion(prev => ({
                ...prev,
                [lista]: (prev[lista] as { Id: string }[]).map(e => e.Id === id ? { ...e, [campo]: valor } : e)
            })),
            alIniciarEdicion: (id) => {
                setRespaldoFila((equipos.find(e => e.Id === id) as unknown as IEquipoServicioAscensor | IEquipoServicioEscalera) ?? null);
                setIntentoGuardar(false);
                setFilaEnEdicion({ tipo, id });
            },
            alGuardar: (id) => {
                const equipo = equipos.find(e => e.Id === id);
                if (!equipo || !completo(equipo)) {
                    setIntentoGuardar(true);
                    mostrarNotificacion(EnumTipoNotificacion.Warning, 'Campos obligatorios', 'Completa todos los campos del equipo antes de guardar la fila.');
                    return;
                }
                // TODO back: guardar el equipo (crear o actualizar) solo aquí, con la fila completa.
                setFilaEnEdicion(null);
                setRespaldoFila(null);
                setIntentoGuardar(false);
            },
            alCancelar: cancelarEdicion,
            alEliminar: async (id) => {
                const confirmado = await confirmar({
                    titulo: 'Eliminar equipo',
                    mensaje: '¿Deseas eliminar este equipo de la cotización?',
                    tipo: EnumTipoNotificacion.Warning,
                    textoAceptar: 'Eliminar',
                    textoCancelar: 'Cancelar'
                });
                if (!confirmado) return;
                // TODO back: eliminar el equipo.
                actualizarCotizacion(prev => ({ ...prev, [lista]: (prev[lista] as { Id: string }[]).filter(e => e.Id !== id) }));
            }
        };
    };

    // --- Acciones del resumen ---

    const alCalcular = () => {
        // TODO back: enviar la cotización a calcular y guardar el resultado que devuelva.
        setCotizacion(prev => ({ ...prev, Resultado: resultadoSinCalcular(prev) }));
        setRequiereRecalculo(false);
        mostrarNotificacion(EnumTipoNotificacion.Information, 'Cálculo pendiente', 'Los valores los calculará el servicio del cotizador; por ahora se muestran en cero.', 4000);
    };

    const alGenerarPdf = () => {
        // TODO back: previsualizar el PDF de la cotización.
        mostrarNotificacion(EnumTipoNotificacion.Information, 'Pendiente', 'El PDF se habilitará al conectar el servicio del cotizador.', 3000);
    };

    const guardar = () => {
        if (!validarDatosGenerales()) {
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Datos incompletos', 'Revisa los datos generales de la cotización.');
            return;
        }
        alGuardar(cotizacion);
    };

    return (
        <CrearCotizacionServiciosVista
            cotizacion={cotizacion}
            paises={paises}
            nombrePais={nombrePais}
            porcentajeIva={configuracion.PorcentajeIva}
            errores={errores}
            soloLectura={false}
            hayFilaEnEdicion={filaEnEdicion !== null}
            requiereRecalculo={requiereRecalculo}
            alCambiar={alCambiar}
            metaAscensores={crearMeta<IEquipoServicioAscensor>('ascensor', ascensorCompleto)}
            metaEscaleras={crearMeta<IEquipoServicioEscalera>('escalera', escaleraCompleta)}
            alAgregarAscensor={() => agregarEquipo('ascensor')}
            alAgregarEscalera={() => agregarEquipo('escalera')}
            alCancelarEdicion={cancelarEdicion}
            validarDatosGenerales={validarDatosGenerales}
            validarEquipos={validarEquipos}
            alCalcular={alCalcular}
            alGenerarPdf={alGenerarPdf}
            alGuardar={guardar}
            alVolver={alVolver}
        />
    );
};
